import { MAX_PLAYERS, SIZES, STREAK_BONUS, type Size } from "../src/protocol";
import type { Lang } from "../src/languages";
import { LANG } from "./i18n";

// Every visible text lives here, once per language. The `Strings` type is derived from the
// English table, so a key missing in Spanish is a compile error.
//
// Wording: WorkBrawl avoids "game"/"play" vocabulary (some workplace filters block it).
// Say round, room, start, people, title, board, cards.

const pairs = (s: Size) => (SIZES[s].cols * SIZES[s].rows) / 2;

const en = {
  appName: "MemoBrawl",
  pageTitle: "MemoBrawl — Shared memory against the clock",
  tagline: "Shared memory against the clock",
  homeIntro: "Open a room, share the link and take turns flipping cards on the same board. Whoever remembers best takes the most pairs.",
  uiLanguageLabel: "Language",
  backToHub: "Back to WorkBrawl",
  privacy: "Privacy",
  terms: "Terms",
  consentText: "We use analytics cookies to learn how WorkBrawl is used. They're off unless you accept.",
  consentAccept: "Accept",
  consentReject: "Reject",
  consentNotice: "We use Google Analytics cookies to understand how WorkBrawl is used and improve it.",
  consentGotIt: "Got it",

  newRoomTitle: "New room",
  nameLabel: "Your name",
  namePlaceholder: "e.g. Alex",
  nameInvalid: "Enter a name of 1 to 20 characters.",
  durationLabel: "Round length",
  minutes: (n: number) => `${n} minutes`,
  sizeLabel: "Board size",
  sizes: {
    small: `Small · ${pairs("small")} pairs`,
    medium: `Medium · ${pairs("medium")} pairs`,
    large: `Large · ${pairs("large")} pairs`,
    xlarge: `Extra large · ${pairs("xlarge")} pairs`,
  } satisfies Record<Size, string>,
  sizeNames: { small: "Small", medium: "Medium", large: "Large", xlarge: "Extra large" } satisfies Record<Size, string>,
  sizeHint: "The board grows by itself when more people join.",
  roundsLabel: "Rounds",
  roundsOption: (n: number) => (n === 1 ? "1 round" : `${n} rounds, with final standings`),
  createRoom: "Create room",
  creatingRoom: "Creating…",
  verifying: "Checking…",
  or: "or",
  codeLabel: "Room code",
  codePlaceholder: "ABCDE",
  codeInvalid: "Room codes have 5 letters or numbers.",
  join: "Join",
  createFailed: "Could not create the room. Try again.",
  joinFailed: "Could not check the room. Try again.",
  turnstileFailed: "The security check failed. Try again.",
  turnstileUnavailable: "The security check is unavailable. Reload the page and try again.",
  turnstileInteract: "Tick the box below to confirm you're a person.",
  errorRoomNotFound: "This room does not exist or has expired.",
  errorRoomFull: `This room is full (${MAX_PLAYERS} people).`,
  invalidRoomLink: "That room link isn't valid.",

  howTitle: "How it works",
  howSteps: [
    { title: "Open a room.", text: "Pick the round length, the board size and how many rounds, then share the link or the QR code." },
    { title: "Take turns.", text: "On your turn, flip two cards; everyone sees them. A pair is yours and you go again; otherwise the turn passes on." },
    { title: "Take the pairs.", text: `Each pair is 1 point, plus ${STREAK_BONUS} for every pair you find right after another. The round ends when every pair is found or time is up.` },
  ],
  faqTitle: "Frequently asked questions",
  faq: [
    { q: "Is MemoBrawl free?", a: "Yes. It's free, needs no account or download and runs in any modern browser." },
    { q: "How many people fit in a room?", a: `Up to ${MAX_PLAYERS} people per room, each on their own device.` },
    { q: "Whose turn is it?", a: "The board says so in the color of that person, and the scoreboard marks them. Everyone else waits; each turn has a few seconds, so nobody holds up the room." },
    { q: "What happens with a wrong pair?", a: "Both cards stay face up for a moment so everyone can memorize them, then turn back over and the turn passes to the next person." },
    { q: "What if there are many of us?", a: "The board grows by itself: medium from 4 people, large from 7 and extra large (24 pairs) from 10. Every card has a number, so you can call them out." },
    { q: "Does it work on a phone?", a: "Yes. Tap a card to flip it." },
  ],

  namePromptTitle: "What's your name?",
  namePromptBody: "This is how the others will see you on the scoreboard.",
  enterRoom: "Enter room",

  room: "Room",
  exit: "Leave",
  cancel: "Cancel",
  exitTitle: "Leave the room?",
  exitBody: "You'll go back to the start screen. Come back with the room link to rejoin.",
  scoreboard: "Scoreboard",
  timeLeft: "Time left",
  you: "you",
  offline: "away",
  points: (n: number) => (n === 1 ? "1 point" : `${n} points`),

  inviteTitle: "Invite your team",
  copyLink: "Copy link",
  linkCopied: "Copied!",
  linkCopiedToast: "Link copied",
  copyFailed: "Copy this link:",
  copyFailedShort: "Couldn't copy",
  share: "Share",
  shareText: "Join me on MemoBrawl: shared memory against the clock!",
  close: "Close",
  qrLabel: (code: string) => `QR code to join room ${code}`,

  lobbyTitle: "Ready when you are",
  lobbyBody: (n: number) => (n === 1 ? "You're the only one here. Share the link, or start on your own." : `${n} people in the room.`),
  startRound: "Start round",
  getReady: "Get ready",
  pairsLeft: (n: number) => (n === 1 ? "1 pair left" : `${n} pairs left`),
  clearedBanner: "Every pair found!",
  yourTurn: "Your turn!",
  turnOf: (name: string) => `${name}'s turn`,
  waitTurn: "wait for your turn",
  turnTag: "turn",
  turnTime: "Time left in this turn",
  missBanner: "Not a pair",
  boardLabel: "Cards",
  cardDown: (n: number) => `Card ${n}, face down`,
  cardUp: (n: number, symbol: string) => `Card ${n}: ${symbol}`,
  cardTaken: (n: number, symbol: string, name: string) => `Card ${n}: ${symbol}, pair found by ${name}`,
  resultsTitle: "Round over!",
  roundOf: (n: number, total: number) => `Round ${n} of ${total}`,
  roundDone: (n: number, total: number) => `Round ${n} of ${total} done`,
  finalResults: "Final results",
  seriesYouWon: "You won the series!",
  seriesWinner: (name: string) => `${name} wins the series`,
  seriesTie: "The series ends in a tie",
  standingsSoFar: "Standings so far",
  standingsTitle: "Points per round",
  roundColumn: (n: number) => `R${n}`,
  totalColumn: "Total",
  nextRound: (n: number, total: number) => `Next round (${n} of ${total})`,
  newSeries: "New series",
  winner: (name: string) => `${name} wins`,
  youWin: "You win!",
  tie: "It's a tie",
  newRound: "New round",

  connecting: "Connecting…",
  reconnecting: "Connection lost. Reconnecting…",
  backHome: "Back to start",
};

export type Strings = typeof en;

const es: Strings = {
  appName: "MemoBrawl",
  pageTitle: "MemoBrawl — Memoria compartida contra el reloj",
  tagline: "Memoria compartida contra el reloj",
  homeIntro: "Abre una sala, comparte el enlace y volteen cartas por turnos en el mismo tablero. Quien mejor memorice se lleva más parejas.",
  uiLanguageLabel: "Idioma",
  backToHub: "Volver a WorkBrawl",
  privacy: "Privacidad",
  terms: "Términos",
  consentText: "Usamos cookies de analítica para saber cómo se usa WorkBrawl. Están desactivadas salvo que las aceptes.",
  consentAccept: "Aceptar",
  consentReject: "Rechazar",
  consentNotice: "Usamos cookies de Google Analytics para entender cómo se usa WorkBrawl y mejorarlo.",
  consentGotIt: "Entendido",

  newRoomTitle: "Nueva sala",
  nameLabel: "Tu nombre",
  namePlaceholder: "p. ej. Álex",
  nameInvalid: "Escribe un nombre de 1 a 20 caracteres.",
  durationLabel: "Duración de la ronda",
  minutes: (n: number) => `${n} minutos`,
  sizeLabel: "Tamaño del tablero",
  sizes: {
    small: `Pequeño · ${pairs("small")} parejas`,
    medium: `Mediano · ${pairs("medium")} parejas`,
    large: `Grande · ${pairs("large")} parejas`,
    xlarge: `Extra grande · ${pairs("xlarge")} parejas`,
  },
  sizeNames: { small: "Pequeño", medium: "Mediano", large: "Grande", xlarge: "Extra grande" },
  sizeHint: "El tablero crece solo cuando entran más personas.",
  roundsLabel: "Rondas",
  roundsOption: (n: number) => (n === 1 ? "1 ronda" : `${n} rondas, con resultado final`),
  createRoom: "Crear sala",
  creatingRoom: "Creando…",
  verifying: "Verificando…",
  or: "o",
  codeLabel: "Código de sala",
  codePlaceholder: "ABCDE",
  codeInvalid: "Los códigos de sala tienen 5 letras o números.",
  join: "Entrar",
  createFailed: "No se pudo crear la sala. Inténtalo de nuevo.",
  joinFailed: "No se pudo comprobar la sala. Inténtalo de nuevo.",
  turnstileFailed: "La verificación de seguridad falló. Inténtalo de nuevo.",
  turnstileUnavailable: "La verificación de seguridad no está disponible. Recarga la página e inténtalo de nuevo.",
  turnstileInteract: "Marca la casilla de abajo para confirmar que eres una persona.",
  errorRoomNotFound: "Esta sala no existe o ya expiró.",
  errorRoomFull: `Esta sala está llena (${MAX_PLAYERS} personas).`,
  invalidRoomLink: "El enlace de la sala no es válido.",

  howTitle: "Cómo funciona",
  howSteps: [
    { title: "Abre una sala.", text: "Elige la duración de la ronda, el tamaño del tablero y cuántas rondas, y comparte el enlace o el código QR." },
    { title: "Por turnos.", text: "En tu turno voltea dos cartas; todos las ven. Si son pareja es tuya y vuelves a voltear; si no, el turno pasa a la siguiente persona." },
    { title: "Llévate las parejas.", text: `Cada pareja vale 1 punto, más ${STREAK_BONUS} por cada pareja que encuentres justo después de otra. La ronda termina cuando se encuentran todas las parejas o se acaba el tiempo.` },
  ],
  faqTitle: "Preguntas frecuentes",
  faq: [
    { q: "¿MemoBrawl es gratis?", a: "Sí. Es gratis, no necesita cuenta ni descarga y funciona en cualquier navegador moderno." },
    { q: "¿Cuántas personas caben en una sala?", a: `Hasta ${MAX_PLAYERS} personas por sala, cada una en su propio dispositivo.` },
    { q: "¿A quién le toca?", a: "El tablero lo dice con el color de esa persona, y el marcador la resalta. Los demás esperan; cada turno tiene unos segundos para que nadie detenga la sala." },
    { q: "¿Qué pasa con una pareja equivocada?", a: "Las dos cartas se quedan boca arriba un momento para que todos las memoricen, luego se voltean y el turno pasa a la siguiente persona." },
    { q: "¿Y si somos muchos?", a: "El tablero crece solo: mediano desde 4 personas, grande desde 7 y extra grande (24 parejas) desde 10. Cada carta tiene un número para que puedan nombrarlas." },
    { q: "¿Funciona en el teléfono?", a: "Sí. Toca una carta para voltearla." },
  ],

  namePromptTitle: "¿Cómo te llamas?",
  namePromptBody: "Así te verán los demás en el marcador.",
  enterRoom: "Entrar a la sala",

  room: "Sala",
  exit: "Salir",
  cancel: "Cancelar",
  exitTitle: "¿Salir de la sala?",
  exitBody: "Volverás al inicio. Puedes regresar con el enlace de la sala.",
  scoreboard: "Marcador",
  timeLeft: "Tiempo restante",
  you: "tú",
  offline: "ausente",
  points: (n: number) => (n === 1 ? "1 punto" : `${n} puntos`),

  inviteTitle: "Invita a tu equipo",
  copyLink: "Copiar enlace",
  linkCopied: "¡Copiado!",
  linkCopiedToast: "Enlace copiado",
  copyFailed: "Copia este enlace:",
  copyFailedShort: "No se pudo copiar",
  share: "Compartir",
  shareText: "¡Únete a MemoBrawl: memoria compartida contra el reloj!",
  close: "Cerrar",
  qrLabel: (code: string) => `Código QR para entrar a la sala ${code}`,

  lobbyTitle: "Cuando quieran",
  lobbyBody: (n: number) => (n === 1 ? "Por ahora solo estás tú. Comparte el enlace, o empieza por tu cuenta." : `${n} personas en la sala.`),
  startRound: "Empezar ronda",
  getReady: "Prepárate",
  pairsLeft: (n: number) => (n === 1 ? "Queda 1 pareja" : `Quedan ${n} parejas`),
  clearedBanner: "¡Encontraron todas las parejas!",
  yourTurn: "¡Tu turno!",
  turnOf: (name: string) => `Turno de ${name}`,
  waitTurn: "espera tu turno",
  turnTag: "turno",
  turnTime: "Tiempo que queda del turno",
  missBanner: "No es pareja",
  boardLabel: "Cartas",
  cardDown: (n: number) => `Carta ${n}, boca abajo`,
  cardUp: (n: number, symbol: string) => `Carta ${n}: ${symbol}`,
  cardTaken: (n: number, symbol: string, name: string) => `Carta ${n}: ${symbol}, pareja de ${name}`,
  resultsTitle: "¡Ronda terminada!",
  roundOf: (n: number, total: number) => `Ronda ${n} de ${total}`,
  roundDone: (n: number, total: number) => `Ronda ${n} de ${total} terminada`,
  finalResults: "Resultados finales",
  seriesYouWon: "¡Ganaste la serie!",
  seriesWinner: (name: string) => `${name} gana la serie`,
  seriesTie: "La serie termina en empate",
  standingsSoFar: "Acumulado hasta ahora",
  standingsTitle: "Puntos por ronda",
  roundColumn: (n: number) => `R${n}`,
  totalColumn: "Total",
  nextRound: (n: number, total: number) => `Siguiente ronda (${n} de ${total})`,
  newSeries: "Nueva serie",
  winner: (name: string) => `Gana ${name}`,
  youWin: "¡Ganaste!",
  tie: "Empate",
  newRound: "Nueva ronda",

  connecting: "Conectando…",
  reconnecting: "Se perdió la conexión. Reconectando…",
  backHome: "Volver al inicio",
};

const TABLES: Record<Lang, Strings> = { en, es };

export const STRINGS: Strings = TABLES[LANG];

/** Each language written in itself, for the language switcher. */
export const LANGUAGE_NAMES: Record<Lang, string> = { en: "English", es: "Español" };
